import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Expense, type NewExpense, type User } from "./api";
import { useViewport } from "./hooks";
import { LoginScreen } from "./screens/Login";
import { SignupScreen } from "./screens/Signup";
import { HomeScreen } from "./screens/Home";
import { AddExpenseModal } from "./screens/AddExpense";
import { SettingsScreen } from "./screens/Settings";

type Screen = "loading" | "login" | "signup" | "home" | "settings";

export function BudgetApp() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const { isDesktop } = useViewport();

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  const loadExpenses = useCallback(async () => {
    try {
      const list = await api.listExpenses();
      setExpenses(list);
    } catch {
      setExpenses([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await api.me();
        setUser(u);
        await loadExpenses();
        setScreen("home");
      } catch {
        setScreen("login");
      }
    })();
  }, [loadExpenses]);

  const handleSignedIn = async (signedIn: User) => {
    setUser(signedIn);
    await loadExpenses();
    setScreen("home");
  };

  const handleSignedUp = async (created: User) => {
    setUser(created);
    setExpenses([]);
    setScreen("home");
    setTimeout(() => setAddOpen(true), 350);
  };

  const addExpense = async (exp: NewExpense) => {
    if (!user) return;
    try {
      const created = await api.addExpense(exp);
      setExpenses((prev) => [created, ...prev]);
      setAddOpen(false);
      showToast("Expense added");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to add");
    }
  };

  const deleteExpense = async (id: string) => {
    const prev = expenses;
    setExpenses(prev.filter((e) => e.id !== id));
    try {
      await api.deleteExpense(id);
    } catch {
      setExpenses(prev);
      showToast("Delete failed");
    }
  };

  const updateUser = async (
    patch: Partial<Pick<User, "username" | "budget" | "quickAdds" | "categories">>,
  ) => {
    if (!user) return;
    try {
      const updated = await api.updateMe(patch);
      setUser(updated);
      showToast("Saved");
    } catch (e) {
      if (e instanceof ApiError) throw e;
      throw new Error("Save failed");
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore — proceed with local clear */
    }
    setUser(null);
    setExpenses([]);
    setScreen("login");
  };

  const resetAll = async () => {
    if (user) {
      try {
        await api.deleteMe();
      } catch {
        /* ignore — proceed with local reset */
      }
    }
    setUser(null);
    setExpenses([]);
    setScreen("signup");
  };

  return (
    <div className="app-shell">
      {screen === "loading" && <LoadingState />}

      {screen === "login" && (
        <LoginScreen
          isDesktop={isDesktop}
          onSignedIn={handleSignedIn}
          onSwitchToSignup={() => setScreen("signup")}
        />
      )}

      {screen === "signup" && (
        <SignupScreen
          isDesktop={isDesktop}
          onSignedUp={handleSignedUp}
          onSwitchToLogin={() => setScreen("login")}
        />
      )}

      {screen === "home" && user && (
        <HomeScreen
          user={user}
          expenses={expenses}
          isDesktop={isDesktop}
          onOpenAdd={() => setAddOpen(true)}
          onOpenSettings={() => setScreen("settings")}
          onDeleteExpense={deleteExpense}
        />
      )}

      {screen === "settings" && user && (
        <SettingsScreen
          user={user}
          isDesktop={isDesktop}
          onUpdate={updateUser}
          onBack={() => setScreen("home")}
          onReset={resetAll}
          onLogout={logout}
        />
      )}

      {addOpen && user && (
        <AddExpenseModal
          username={user.username}
          categories={user.categories ?? []}
          quickAdds={user.quickAdds ?? []}
          isDesktop={isDesktop}
          onClose={() => setAddOpen(false)}
          onAdd={addExpense}
        />
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 50,
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--ink)",
            color: "white",
            padding: "10px 18px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--accent)" }} />
          {toast}
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--muted)",
        fontSize: 13,
        letterSpacing: ".08em",
        textTransform: "uppercase",
      }}
    >
      Loading
    </div>
  );
}
